import { IJsonModel, Model, Actions, DockLocation } from 'flexlayout-react';

/**
 * Add a widget to the layout programmatically
 * @param model - FlexLayout model instance
 * @param widgetId - Widget ID from registry
 * @param widgetName - Display name for the tab
 * @param config - Optional widget configuration
 */
export function addWidgetToLayout(
  model: Model,
  widgetId: string,
  widgetName: string,
  config?: Record<string, unknown>
): void {
  model.doAction(
    Actions.addNode(
      {
        type: 'tab',
        component: widgetId,
        name: widgetName,
        config: config || {},
      },
      'root',
      DockLocation.CENTER,
      -1
    )
  );
}

/**
 * Create a default FlexLayout model with no widgets
 * Apps can use this as a starting point
 */
export function createDefaultModel(): IJsonModel {
  return {
    global: {
      tabEnableClose: true,
      tabEnableRename: false,
      tabSetEnableMaximize: true,
      tabSetEnableDrop: true,
      tabSetEnableDeleteWhenEmpty: true,
      tabSetEnableDrag: true,
      tabSetEnableDivide: true,
      borderEnableDrop: true,
    },
    borders: [],
    layout: {
      type: 'row',
      weight: 100,
      children: [],
    },
  };
}

/**
 * Create a model with example widgets pre-loaded
 * Useful for demos and testing
 */
export function createExampleModel(): IJsonModel {
  return {
    global: {
      tabEnableClose: true,
      tabEnableRename: false,
      tabSetEnableMaximize: true,
      tabSetEnableDrop: true,
      tabSetEnableDeleteWhenEmpty: true,
      tabSetEnableDrag: true,
      tabSetEnableDivide: true,
      borderEnableDrop: true,
    },
    borders: [],
    layout: {
      type: 'row',
      weight: 100,
      children: [
        {
          type: 'tabset',
          weight: 50,
          children: [
            {
              type: 'tab',
              name: 'Chart',
              component: 'chart-widget',
              config: {},
            },
          ],
        },
        {
          type: 'tabset',
          weight: 50,
          children: [
            {
              type: 'tab',
              name: 'Positions',
              component: 'positions-widget',
              config: {},
            },
            {
              type: 'tab',
              name: 'Orders',
              component: 'orders-widget',
              config: {},
            },
          ],
        },
      ],
    },
  };
}

/**
 * Create a trading-focused layout with chart, positions, orders, and metrics
 */
export function createTradingLayout(): IJsonModel {
  return {
    global: {
      tabEnableClose: true,
      tabEnableRename: false,
      tabSetEnableMaximize: true,
      tabSetEnableDrop: true,
      tabSetEnableDeleteWhenEmpty: true,
      tabSetEnableDrag: true,
      tabSetEnableDivide: true,
      borderEnableDrop: true,
    },
    borders: [],
    layout: {
      type: 'row',
      weight: 100,
      children: [
        {
          type: 'column',
          weight: 70,
          children: [
            {
              type: 'tabset',
              weight: 60,
              children: [
                {
                  type: 'tab',
                  name: 'Chart',
                  component: 'chart-widget',
                  config: {},
                },
              ],
            },
            {
              type: 'tabset',
              weight: 40,
              children: [
                {
                  type: 'tab',
                  name: 'Orders',
                  component: 'orders-widget',
                  config: {},
                },
              ],
            },
          ],
        },
        {
          type: 'column',
          weight: 30,
          children: [
            {
              type: 'tabset',
              weight: 40,
              children: [
                {
                  type: 'tab',
                  name: 'Metrics',
                  component: 'metrics-widget',
                  config: {},
                },
              ],
            },
            {
              type: 'tabset',
              weight: 60,
              children: [
                {
                  type: 'tab',
                  name: 'Positions',
                  component: 'positions-widget',
                  config: {},
                },
              ],
            },
          ],
        },
      ],
    },
  };
}

/**
 * Save layout model to localStorage
 */
export function saveLayoutToLocalStorage(key: string, model: IJsonModel): void {
  try {
    localStorage.setItem(key, JSON.stringify(model));
  } catch (error) {
    console.error('Failed to save layout to localStorage:', error);
  }
}

/**
 * Load layout model from localStorage
 * Returns undefined if not found or invalid
 */
export function loadLayoutFromLocalStorage(key: string): IJsonModel | undefined {
  try {
    const data = localStorage.getItem(key);
    if (!data) return undefined;
    return JSON.parse(data) as IJsonModel;
  } catch (error) {
    console.error('Failed to load layout from localStorage:', error);
    return undefined;
  }
}

/**
 * Get layout storage key for a specific app/user
 */
export function getLayoutStorageKey(appName: string, userId?: string): string {
  return userId ? `pulse-layout-${appName}-${userId}` : `pulse-layout-${appName}`;
}
